import { isReservedUsageEventField } from '@monetize/constants';
import { isValidUsagePropertyKey } from '../usageProperties.utils';

const PROPERTIES_PREFIX = 'properties.';

export interface ExtractedCsvProperties {
  properties: Record<string, string> | undefined;
  // Property column names (without the `properties.` prefix) that hold a value
  // but fail the identifier rule. Surfaced as row errors so the upload matches
  // the validation the usage service applies.
  invalidKeys: string[];
  // Property column names that hold a value but collide with a name the usage
  // service reserves for its own columns (`id`, `subscription_id`, ...).
  reservedKeys: string[];
}

/**
 * Pulls `properties.<key>` columns out of a parsed CSV row into a flat
 * key/value object. Empty/whitespace-only values are skipped so the resulting
 * payload omits keys with no value (the BE treats `properties` as
 * write-on-create — sending an empty string for a property would persist it).
 *
 * Keys that hold a value but aren't valid identifiers are collected in
 * `invalidKeys` rather than included in `properties`, so the caller can reject
 * the row up front instead of relying on a server-side rejection.
 */
export function extractPropertiesFromCsvRow(
  row: Record<string, unknown>,
): ExtractedCsvProperties {
  const properties: Record<string, string> = {};
  const invalidKeys: string[] = [];
  const reservedKeys: string[] = [];
  for (const [header, raw] of Object.entries(row)) {
    if (!header.startsWith(PROPERTIES_PREFIX)) {
      continue;
    }
    const key = header.slice(PROPERTIES_PREFIX.length).trim();
    if (!key) {
      continue;
    }
    const value = typeof raw === 'string' ? raw.trim() : '';
    if (!value) {
      continue;
    }
    if (!isValidUsagePropertyKey(key)) {
      invalidKeys.push(key);
      continue;
    }
    if (isReservedUsageEventField(key)) {
      reservedKeys.push(key);
      continue;
    }
    properties[key] = value;
  }
  return {
    properties: Object.keys(properties).length > 0 ? properties : undefined,
    invalidKeys,
    reservedKeys,
  };
}
